import { cloneDeep, get, merge } from "lodash";

import { ApiService, Clinic, EMR } from "pwapi";

import { ClinicList } from "custom-models/frontLoader";

import { CaseAdminDetail } from "reducers/admin/adminCaseReducer";

// Hard-coded defaults based on previously hard-coded *_clinic_id fields
// This allows us to use one format based on the ClinicEMRMap for all components
// If a clinic doesn't have a new EMR mapping, return default fields as if it did
// Thus the component can be "dumb" and doesn't/shouldn't know better,
// ... and we're not branching if/else or duplicating fields
export const defaultFields = {
  ModMed: [
    {
      integration_attribute: "external_order_id",
      label: "ModMed Order Number",
      required: true,
    },
    {
      integration_attribute: "patient.external_patient_id",
      label: "EMA ID",
      required: true,
    },
    {
      integration_attribute: "patient.medical_record_number",
      label: "Medical Record Number",
      required: true,
    },
  ],
  EZDerm: [
    {
      integration_attribute: "external_ezderm_order_id",
      label: "EZDerm Order Number",
      required: true,
    },
    {
      integration_attribute: "patient.external_patient_id",
      label: "External MRN",
      required: false,
    },
  ],
  Nextech: [
    {
      integration_attribute: "external_order_id",
      label: "NexTech Order Number",
      required: true,
    },
    {
      integration_attribute: "patient.external_patient_id",
      label: "Encounter ID",
      required: true,
    },
    {
      integration_attribute: "patient.medical_record_number",
      label: "Medical Record Number",
      required: true,
    },
  ],
  EClinicalWorks: [
    {
      label: "Req/Ctrl#",
      required: true,
      integration_attribute: "external_order_id",
    },
    {
      label: "Encounter ID",
      required: true,
      integration_attribute: "patient.external_patient_id",
    },
    {
      label: "Medical Record Number",
      required: true,
      integration_attribute: "patient.medical_record_number",
    },
  ],
};

const legacyEMRFields = (clinic?: Clinic) => {
  if (!clinic) return undefined;
  const name = legacyEMRName(clinic);
  return {
    name,
    fields: name ? defaultFields[name] : undefined,
  };
};

const legacyEMRName = (clinic?: Clinic) => {
  if (!clinic) return undefined;
  return clinic.modmed_clinic_id
    ? "ModMed"
    : clinic.ecw_clinic_id
    ? "EClinicalWorks"
    : clinic.nextech_clinic_id
    ? "Nextech"
    : clinic.ezderm_client_id
    ? "EZDerm"
    : undefined;
};

// TODO: we are destroying order because fields isn't an array to begin with
// JSON has no ordering of attributes in objects
export const inputPropsFromField = (field: EMR["fields"][0], flat = false) => {
  const { integration_attribute, label, required } = field;
  return {
    id: integration_attribute.split(".").at(-1) || integration_attribute,
    name: flat
      ? integration_attribute.split(".").at(-1) || integration_attribute
      : integration_attribute,
    label,
    required,
  };
};

export const objectStructureForEMRFields = (
  emr: ReturnType<typeof useClinicFields>,
  blankValue = null
) => {
  if (!emr?.fields) return {};
  return Object.fromEntries(emr.fields.map((f) => [f.name, blankValue]));
};

export const objectValueForFieldName = (
  name: string,
  value: string | { [key: string]: string | object }
) => {
  // Takes a path like "patient.external_patient_id"
  // And transforms it into an object like { patient: { external_patient_id: value } }
  const segments = name.split(".");
  return segments.reverse().reduce((object, segment) => {
    return { [segment]: object };
  }, value);
};

export const integrationObjectForUpdate = (
  name: string,
  value: string | {},
  values: { [key: string]: string | object }
) => {
  const objectPath = objectValueForFieldName(name, value);
  const segments = name.split(".");
  if (
    segments.length > 1 &&
    segments.at(0) &&
    values.hasOwnProperty(segments.at(0) || "") &&
    typeof objectPath === "object"
  ) {
    const propertyName = segments.at(0);
    if (!propertyName)
      throw Error(
        `Unable to deep merge ${name} with no starting path for ${value}`
      );
    const deepClone = cloneDeep(values[propertyName]);
    merge(deepClone, objectPath[propertyName]);
    return { [propertyName]: deepClone };
  }

  return objectPath;
};

export const isMRNRequired = (emr: ReturnType<typeof useClinicFields>) => {
  return emr?.fields?.find(
    (f) =>
      f.name?.includes("medical_record_number") ||
      f.label.includes("Medical Record Number")
  )?.required;
};

export const isSpecimenScannable = (
  clinic?: Clinic | ClinicList
): boolean | undefined => {
  return !clinic?.emrs?.at(0) || clinic?.emrs?.at(0)?.scannable_specimen;
};

export const useIntegrationValues = ({
  adminCaseOrValues,
  clinic,
  skipMRN = false,
  flatIntegration = false,
}: {
  adminCaseOrValues?:
    | Partial<CaseAdminDetail>
    | Parameters<
        typeof ApiService.partialUpdateIntegrationAdminViewSet
      >[0]["requestBody"];
  clinic?: Clinic;
  skipMRN?: boolean;
  flatIntegration?: boolean;
}) => {
  const emr = useClinicFields({
    clinic:
      clinic || adminCaseOrValues?.clinic || adminCaseOrValues.patient?.clinic,
    ordering_doctor_id: adminCaseOrValues?.ordering_doctor?.id,
    flatIntegration,
    skipMRN,
  });
  const values = emr?.fields
    ? Object.fromEntries(
        emr?.fields?.map((field) => {
          const flatPath = field.name.split(".").at(-1);
          return [flatPath, get(adminCaseOrValues, field.name)];
        })
      )
    : {};
  return values;
};

const useClinicFields = ({
  clinic,
  ordering_doctor_id,
  skipMRN = false,
  flatIntegration = false,
}: {
  clinic?: Clinic;
  ordering_doctor_id?: number;
  skipMRN?: boolean;
  flatIntegration?: boolean;
}) => {
  // Hack-y workaround to hide EMR fields for non-integrated providers at EMR clinics
  const skip_provider_ids = clinic?.emr_mappings?.flatMap(
    (em) => em?.settings?.skip_provider_ids || []
  );
  if (ordering_doctor_id && skip_provider_ids?.includes(ordering_doctor_id))
    return undefined;

  // Explicitly Not Supporting Multiple EMRs right now
  // TODO: Support Multiple EMRs
  // Drop down to "legacy" (hard coded) fields if no emr is configured
  const emr = clinic?.emrs?.at(0) || legacyEMRFields(clinic);

  const hasEmptyObjectField =
    emr?.fields?.some(
      (field) =>
        !!field && typeof field === "object" && Object.keys(field).length === 0
    ) || false;

  // Put this into a friendlier format based on what input expects as props
  const fields = hasEmptyObjectField
    ? undefined
    : emr?.fields
    ? emr?.fields
        .map((field) => inputPropsFromField(field, flatIntegration))
        // Option to explicitly skip MRN if don't want to duplicate it in the form
        .filter((f) =>
          skipMRN ? (f.name.includes("medical_record_number") ? false : f) : f
        )
    : undefined;

  return emr?.fields
    ? {
        emrName: emr?.name,
        fields,
      }
    : undefined;
};
export type ClinicEMR = ReturnType<typeof useClinicFields>;

export default useClinicFields;
